import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, BadgeCheck, MapPin, Clock, MessageSquare, Calendar, ChevronRight, Users, Image, ExternalLink, Store, Share2, Flag, Crown, Music, Briefcase, X } from 'lucide-react';
import BookNowPicker from '@/components/booking/BookNowPicker';
import VendorIntroVideo from '@/components/VendorIntroVideo';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { publicUrl } from '@/lib/publicUrl';
import ReportIssueModal from '@/components/ReportIssueModal';
import useSeo from '@/hooks/useSeo';
import FollowButton from '@/components/social/FollowButton';
import VendorSocialStrip from '@/components/social/VendorSocialStrip';
import VendorPostsWall from '@/components/social/VendorPostsWall';
import StoryRail from '@/components/social/StoryRail';
import YouTubeEmbed from '@/components/YouTubeEmbed';
import { formatEthnicity } from '@/utils/ethnicity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function abs(url) {
  if (!url) return '';
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

/** Inline "Shop" tab — lists this vendor's marketplace items in a mini
 * Facebook-marketplace style grid. Clicking a card goes to the item
 * detail page where the buyer can add to cart & checkout. */
function VendorShopTab({ vendorId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`${API}/public/items/browse?vendor_id=${vendorId}&limit=60`)
      .then((r) => r.json())
      .then((data) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [vendorId]);

  if (loading) return <p className="text-stone-400 text-sm py-6">Loading shop…</p>;
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 py-12 text-center text-stone-400">
        <Store size={40} className="mx-auto mb-3 opacity-40" />
        <p className="text-stone-200 font-semibold">No items for sale yet</p>
        <p className="text-xs mt-1">This vendor hasn&apos;t listed any Shop items.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10" data-testid="vendor-shop-grid">
      {items.map((it) => {
        const img = (it.images && it.images[0]) || '';
        return (
          <Link key={it.item_id} to={`/shop/item/${it.item_id}`} className="group relative" data-testid={`vendor-shop-item-${it.item_id}`}>
            <div className="absolute inset-x-3 -bottom-2 h-10 rounded-[32px] blur-2xl opacity-80 bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#C4B5FD] group-hover:opacity-100 transition-opacity" aria-hidden />
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#1A1D4A] border border-white/5 shadow-[0_10px_30px_-10px_rgba(139,92,246,0.6)]">
              {img ? (
                <img src={abs(img)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]"><Store size={40} /></div>
              )}
              {it.in_stock === false && (
                <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                  <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white text-[#0E0E23]">Sold out</span>
                </div>
              )}
            </div>
            <div className="mt-3 px-1">
              <p className="font-syne text-sm font-semibold text-white truncate group-hover:text-[#C4B5FD]">{it.title}</p>
              <p className="text-xs text-stone-300 font-semibold mt-0.5">${Number(it.base_price).toFixed(2)}</p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export default function VendorProfilePage() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [showBookPicker, setShowBookPicker] = useState(false);
  const [presence, setPresence] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);

  // ── Per-vendor SEO / rich snippet ──
  useSeo({
    title: vendor ? `${vendor.business_name || vendor.owner_name} · Indigenous Vendor` : undefined,
    description: vendor
      ? `${vendor.business_name || vendor.owner_name} — ${vendor.tribal_affiliation ? `${vendor.tribal_affiliation} · ` : ''}${vendor.description ? vendor.description.slice(0, 160) : 'Verified Indigenous-owned business on EntrepreNatives.'}`
      : undefined,
    image: vendor?.profile_image || vendor?.banner_image || '/og-image.png',
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    jsonLd: vendor ? {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: vendor.business_name || vendor.owner_name,
      description: vendor.description || 'Indigenous-owned business',
      image: vendor.profile_image || vendor.banner_image,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      address: vendor.service_city ? {
        '@type': 'PostalAddress',
        addressLocality: vendor.service_city,
        addressRegion: vendor.service_state,
        addressCountry: vendor.service_country || 'US',
      } : undefined,
      aggregateRating: vendor.rating && vendor.review_count ? {
        '@type': 'AggregateRating',
        ratingValue: vendor.rating,
        reviewCount: vendor.review_count,
      } : undefined,
      makesOffer: (vendor.services || []).slice(0, 5).map((s) => ({
        '@type': 'Offer',
        name: s.title,
        description: s.description,
      })),
    } : undefined,
  });

  useEffect(() => {
    if (!vendorId) return;
    fetch(`${API}/vendors/${vendorId}/presence`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setPresence(d))
      .catch(() => {});
  }, [vendorId]);

  const handleShare = async () => {
    const url = publicUrl(`/vendor/${vendorId}`);
    const title = vendor?.business_name ? `${vendor.business_name} on EntrepreNatives` : 'EntrepreNatives vendor';
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch {
      /* user cancelled */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  useEffect(() => {
    fetchVendor();
  }, [vendorId, user]);

  // Auto-open booking modal when returning from auth via ?action=book or ?action=custom-request
  useEffect(() => {
    if (!user || !vendor) return;
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action === 'book' || action === 'custom-request') {
      setShowBookPicker(true);
      // clean URL so refresh doesn't re-trigger
      params.delete('action');
      const q = params.toString();
      window.history.replaceState({}, '', `/vendor/${vendorId}${q ? `?${q}` : ''}`);
    }
  }, [user, vendor, vendorId]);

  const fetchVendor = async () => {
    try {
      const response = await fetch(`${API}/vendors/${vendorId}/`);
      if (response.ok) {
        const data = await response.json();
        setVendor(data);
        // Meta CAPI: ViewContent event
        try {
          window.metaTrack?.('ViewContent', {
            content_ids: [vendorId],
            content_type: 'product',
            content_name: data.business_name || 'Vendor profile',
            content_category: data.main_category_name,
          });
        } catch {}
      } else {
        navigate('/browse');
      }
    } catch (error) {
      console.error('Failed to fetch vendor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = () => {
    if (!user) {
      const target = `/messages/${vendor.user_id}`;
      try { sessionStorage.setItem('postAuthRedirect', target); } catch {}
      toast.info('Create a free account to message this vendor.');
      navigate(`/auth?mode=register&return=${encodeURIComponent(target)}`);
      return;
    }
    navigate(`/messages/${vendor.user_id}`);
  };

  const handleBook = ({ serviceId, custom = false } = {}) => {
    if (!user) {
      const target = serviceId
        ? `/booking/${serviceId}`
        : `/vendor/${vendorId}?action=${custom ? 'custom-request' : 'book'}`;
      try { sessionStorage.setItem('postAuthRedirect', target); } catch {}
      toast.info(custom
        ? 'Create a free account to send a custom request.'
        : 'Create a free account to book this vendor.');
      navigate(`/auth?mode=register&return=${encodeURIComponent(target)}`);
      return;
    }
    if (serviceId) {
      navigate(`/booking/${serviceId}`);
    } else {
      setShowBookPicker(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E0E23]">
        <Navbar />
        <div className="pt-24 pb-16 container-main">
          <div className="animate-pulse">
            <div className="h-64 bg-[#1E1E3A] rounded-3xl mb-8"></div>
            <div className="h-8 bg-[#2D2D52]/40 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-[#2D2D52]/40 rounded w-1/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!vendor) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23]">
      <Navbar />

      {/* Hero Banner */}
      <section className="pt-16 md:pt-20 relative">
        <div
          className="relative w-full bg-[#1A1A3E] aspect-[16/9] sm:aspect-[21/9] md:aspect-[3/1] lg:aspect-[16/5] max-h-[520px]"
          style={{
            backgroundImage: vendor.banner_image
              ? `url(${vendor.banner_image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${vendor.banner_image}` : vendor.banner_image})`
              : 'linear-gradient(135deg, #1E1E3A 0%, #0E0E23 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
          data-testid="vendor-banner"
        >
          {/* Slim bottom fade for text/card legibility only */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0E0E23] to-transparent pointer-events-none" />
        </div>

        {/* Profile Card — sits just below the banner with a small overlap */}
        <div className="container-main relative -mt-10 md:-mt-12">
          <motion.div
            className="card-vendor flex flex-col md:flex-row gap-5 md:gap-6 items-start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Profile Image — tap to zoom (no clipping on mobile or desktop) */}
            {(() => {
              const picSrc = vendor.profile_image || vendor.user_picture;
              const resolved = picSrc
                ? (picSrc.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${picSrc}` : picSrc)
                : null;
              const clickable = !!resolved;
              return (
                <button
                  type="button"
                  onClick={() => clickable && setZoomOpen(true)}
                  disabled={!clickable}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden bg-[#2D2D52]/40 flex-shrink-0 border-4 border-stone-950 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.6)] hover:brightness-110 active:scale-[0.97] transition-all disabled:cursor-default cursor-zoom-in"
                  data-testid="vendor-profile-image"
                  aria-label={clickable ? 'Zoom profile photo' : 'Profile photo'}
                >
                  {resolved ? (
                    <img src={resolved} alt={vendor.business_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-600">
                      <Users className="w-10 h-10" />
                    </div>
                  )}
                </button>
              );
            })()}

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="font-syne text-2xl md:text-3xl font-bold text-stone-100" data-testid="vendor-name">
                  {vendor.business_name}
                </h1>
                {vendor.is_founding_vendor && (
                  <span
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold text-[#0E0E23] shadow-[0_0_18px_rgba(252,211,77,0.5)]"
                    style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' }}
                    data-testid="founding-vendor-badge"
                    title={`Founding Vendor #${vendor.founding_vendor_number || ''} — one of the original 50 who helped build EntrepreNatives`}
                  >
                    <Crown className="w-3 h-3" /> Founding Vendor {vendor.founding_vendor_number ? `#${vendor.founding_vendor_number}` : ''}
                  </span>
                )}
                {vendor.membership_level === 'elite' && (
                  <span className="badge-elite"><BadgeCheck className="w-3 h-3" /> Elite</span>
                )}
                {vendor.vendor_type === 'verified' && (
                  <span className="badge-verified"><BadgeCheck className="w-3 h-3" /> Verified</span>
                )}
                {vendor.vendor_type === 'startup' && (
                  <span className="badge-startup">Startup</span>
                )}
                {vendor.verification_status === 'submitted' && (
                  <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full text-xs">ID Verified</span>
                )}
              </div>

              {vendor.owner_name && (
                <p className="font-outfit text-stone-300 mb-3" data-testid="vendor-owner-name">
                  by <span className="text-stone-100 font-semibold">{vendor.owner_name}</span>
                  {vendor.gender && (
                    <span className="ml-2 text-[#C4B5FD] text-xs uppercase tracking-wider" data-testid="vendor-gender">
                      · {vendor.gender === 'non_binary' ? 'Non-Binary' : vendor.gender.charAt(0).toUpperCase() + vendor.gender.slice(1)}
                    </span>
                  )}
                </p>
              )}

              {/* Profile Info Order: Industry, Ethnicity, Tribal Affiliation, City/State */}
              <div className="space-y-1 mb-3">
                {vendor.main_category_name && (
                  <p className="font-outfit text-sm" data-testid="vendor-industry">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 text-[#A78BFA] font-semibold">
                      <Briefcase className="w-3.5 h-3.5" />
                      {vendor.main_category_name}
                      {vendor.subcategory_name && (
                        <span className="text-[#C4B5FD]/80 font-normal">· {vendor.subcategory_name}</span>
                      )}
                    </span>
                  </p>
                )}
                {formatEthnicity(vendor.ethnicity) && (
                  <p className="text-[#C4B5FD] font-outfit font-semibold text-sm" data-testid="vendor-ethnicity">
                    {formatEthnicity(vendor.ethnicity)}
                  </p>
                )}
                {vendor.tribal_affiliation && (
                  <p className="text-[#FCD34D] font-outfit font-semibold" data-testid="vendor-tribe">
                    {vendor.tribal_affiliation}
                  </p>
                )}
                <p className="font-outfit text-sm" data-testid="vendor-recognition">
                  {vendor.vendor_type === 'verified' || vendor.ein_number ? (
                    <span className="text-green-400 flex items-center gap-1">
                      <BadgeCheck className="w-4 h-4" /> Verified Business
                    </span>
                  ) : (
                    <span className="text-[#A78BFA] flex items-center gap-1">
                      <Store className="w-4 h-4" /> Startup Vendor
                    </span>
                  )}
                </p>
                <p className="text-[#9CA3AF] font-outfit flex items-center gap-1" data-testid="vendor-location">
                  <MapPin className="w-4 h-4" /> {vendor.location}
                </p>
              </div>

              {/* Bio */}
              {vendor.bio && (
                <p className="text-[#9CA3AF] font-outfit text-sm mb-3 line-clamp-2" data-testid="vendor-bio">
                  {vendor.bio}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-[#9CA3AF] font-outfit text-sm">
                {presence?.online && (
                  <span className="flex items-center gap-1.5 text-green-400 font-medium" data-testid="vendor-online">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
                    </span>
                    Online now
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-[#FCD34D] fill-amber-500" /> 
                  {vendor.rating?.toFixed(1) || 'New'} ({vendor.review_count || 0} reviews)
                </span>
                <span className="flex items-center gap-1" data-testid="vendor-response-time">
                  <Clock className="w-4 h-4" /> {presence?.response_label || vendor.response_time}
                </span>
              </div>

              {/* Intro Video — autoplay (muted) + auto-unmute on first user interaction */}
              {vendor.intro_video_url && (
                <VendorIntroVideo src={`${process.env.REACT_APP_BACKEND_URL}${vendor.intro_video_url}`} />
              )}

              {/* Audio Samples (musicians / producers) */}
              {vendor.audio_tracks?.length > 0 && (
                <div className="mt-5 max-w-2xl" data-testid="vendor-audio-samples">
                  <h3 className="text-sm font-semibold text-[#A78BFA] uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Music className="w-4 h-4" /> Listen to samples · {vendor.audio_tracks.length} track{vendor.audio_tracks.length === 1 ? '' : 's'}
                  </h3>
                  <div className="space-y-2">
                    {vendor.audio_tracks.map((track, idx) => (
                      <div
                        key={track.track_id}
                        className="p-3 rounded-xl bg-white/[0.04] border border-white/10 hover:border-[#8B5CF6]/40 transition"
                        data-testid={`public-audio-${track.track_id}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center text-white text-xs font-bold font-mono">
                            {String(idx + 1).padStart(2, '0')}
                          </div>
                          <p className="text-white text-sm font-medium truncate flex-1">{track.title}</p>
                        </div>
                        <audio
                          src={track.url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${track.url}` : track.url}
                          controls
                          preload="metadata"
                          className="w-full h-9"
                          style={{ filter: 'invert(1) hue-rotate(180deg) brightness(0.9)' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={handleMessage} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold" data-testid="message-vendor-btn">
                  <MessageSquare className="w-5 h-5 mr-2" /> Message Vendor
                </Button>
                {vendor.services?.length > 0 ? (
                  <Button
                    onClick={() => handleBook({})}
                    className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                    data-testid="book-now-btn"
                  >
                    <Calendar className="w-5 h-5 mr-2" /> Book Now
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleBook({ custom: true })}
                    className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                    data-testid="book-custom-only-btn"
                  >
                    <Calendar className="w-5 h-5 mr-2" /> Send Custom Request
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="share-vendor-btn"
                >
                  <Share2 className="w-4 h-4" /> Share
                </Button>
                {vendor.user_id && (
                  <FollowButton targetUserId={vendor.user_id} viewerUserId={user?.user_id} />
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!user) { navigate('/auth'); return; }
                    setShowReport(true);
                  }}
                  className="btn-secondary flex items-center gap-2 text-[#FCA5A5] border-red-500/20 hover:border-red-500/40 hover:bg-red-500/5"
                  data-testid="report-vendor-btn"
                >
                  <Flag className="w-4 h-4" /> Report
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Facebook-style social stats strip */}
          {vendor.user_id && (
            <VendorSocialStrip userId={vendor.user_id} viewerId={user?.user_id} />
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-main">
          <Tabs defaultValue="posts" className="space-y-8">
            <TabsList className="glass p-1 rounded-xl">
              <TabsTrigger value="posts" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-posts">
                Posts
              </TabsTrigger>
              <TabsTrigger value="about" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-about">
                About
              </TabsTrigger>
              <TabsTrigger value="services" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-services">
                Services
              </TabsTrigger>
              <TabsTrigger value="shop" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-shop">
                Shop
              </TabsTrigger>
              <TabsTrigger value="portfolio" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-portfolio">
                Portfolio
              </TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-reviews">
                Reviews
              </TabsTrigger>
            </TabsList>

            {/* Posts Tab — Facebook-style social wall */}
            <TabsContent value="posts">
              {/* Stories rail — vendor can add a story via camera (mobile) or upload */}
              <StoryRail
                viewer={user}
                canPost={!!user && user.user_id === vendor.user_id}
              />
              <VendorPostsWall userId={vendor.user_id} viewer={user} />
            </TabsContent>

            {/* About Tab */}
            <TabsContent value="about">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  {vendor.youtube_url && (
                    <div className="card-vendor" data-testid="vendor-youtube-card">
                      <h2 className="font-syne text-xl font-bold text-stone-100 mb-4 flex items-center gap-2">
                        <span className="w-1 h-5 bg-gradient-to-b from-red-500 to-pink-500 rounded-full"></span>
                        Featured Video
                      </h2>
                      <YouTubeEmbed url={vendor.youtube_url} title={`${vendor.business_name} — featured video`} />
                    </div>
                  )}
                  {vendor.description && (
                    <div className="card-vendor">
                      <h2 className="font-syne text-xl font-bold text-stone-100 mb-4">About the Business</h2>
                      <p className="text-[#9CA3AF] font-outfit whitespace-pre-line" data-testid="vendor-description">
                        {vendor.description}
                      </p>
                    </div>
                  )}
                  {vendor.bio && (
                    <div className="card-vendor border-[#8B5CF6]/20" data-testid="vendor-about-owner">
                      <h2 className="font-syne text-xl font-bold text-stone-100 mb-4 flex items-center gap-2">
                        <span className="w-1 h-5 bg-gradient-to-b from-[#8B5CF6] to-[#3B82F6] rounded-full"></span>
                        About the Owner
                      </h2>
                      <p className="text-[#C4B5FD] font-outfit whitespace-pre-line italic" data-testid="vendor-bio-text">
                        {vendor.bio}
                      </p>
                    </div>
                  )}
                  {!vendor.description && !vendor.bio && (
                    <div className="card-vendor">
                      <p className="text-[#64748B] text-sm text-center py-6">This vendor hasn't added a description yet.</p>
                    </div>
                  )}
                </div>

                <div>
                  <div className="card-vendor">
                    <h3 className="font-syne text-lg font-bold text-stone-100 mb-4">Stats</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-[#9CA3AF] font-outfit">Jobs Completed</span>
                        <span className="text-stone-200 font-mono">{vendor.jobs_completed || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#9CA3AF] font-outfit">Response Time</span>
                        <span className="text-stone-200 font-mono">{vendor.response_time}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#9CA3AF] font-outfit">Member Since</span>
                        <span className="text-stone-200 font-mono">
                          {new Date(vendor.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services">
              {vendor.services?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {vendor.services.map((service) => (
                    <div key={service.service_id} className="card-vendor" data-testid={`service-card-${service.service_id}`}>
                      <h3 className="font-syne text-lg font-bold text-stone-100 mb-2">{service.title}</h3>
                      <p className="text-[#9CA3AF] font-outfit text-sm mb-4 line-clamp-2">{service.description}</p>
                      
                      <div className="space-y-3">
                        {service.packages?.map((pkg) => (
                          <div key={pkg.name} className="flex items-center justify-between p-3 rounded-xl bg-[#2D2D52]/40/50">
                            <div>
                              <span className="text-stone-200 font-outfit font-medium capitalize">{pkg.name}</span>
                              <span className="text-[#64748B] text-sm ml-2">({pkg.delivery_days} days)</span>
                            </div>
                            <span className="text-[#FCD34D] font-mono font-bold">${pkg.price}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        onClick={() => handleBook({ serviceId: service.service_id })}
                        className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold mt-4"
                        data-testid={`book-${service.service_id}`}
                      >
                        Book Now <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card-vendor text-center py-12">
                  <Calendar className="w-12 h-12 text-stone-700 mx-auto mb-4" />
                  <p className="text-[#9CA3AF] font-outfit">No services listed yet</p>
                </div>
              )}
            </TabsContent>

            {/* Shop Tab — marketplace items for sale */}
            <TabsContent value="shop">
              <VendorShopTab vendorId={vendorId} />
            </TabsContent>

            {/* Portfolio Tab */}
            <TabsContent value="portfolio">
              {(vendor.portfolio_images?.length > 0 || vendor.portfolio_videos?.length > 0) ? (
                <div className="space-y-8">
                  {/* Images */}
                  {vendor.portfolio_images?.length > 0 && (
                    <div>
                      <h3 className="font-syne text-lg font-bold text-stone-100 mb-4">Images</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {vendor.portfolio_images.map((img, index) => {
                          const src = img?.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`;
                          return (
                            <div key={index} className="aspect-square rounded-2xl overflow-hidden bg-[#1E1E3A]">
                              <img src={src} alt={`Portfolio ${index + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Videos */}
                  {vendor.portfolio_videos?.length > 0 && (
                    <div>
                      <h3 className="font-syne text-lg font-bold text-stone-100 mb-4">Videos</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vendor.portfolio_videos.map((video, index) => {
                          const src = video?.startsWith('http') ? video : `${process.env.REACT_APP_BACKEND_URL}${video}`;
                          return (
                            <div key={index} className="aspect-video rounded-2xl overflow-hidden bg-[#1E1E3A]">
                              <video 
                                src={src} 
                                controls 
                                playsInline
                                className="w-full h-full object-cover"
                                preload="metadata"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="card-vendor text-center py-12">
                  <Image className="w-12 h-12 text-stone-700 mx-auto mb-4" />
                  <p className="text-[#9CA3AF] font-outfit">No portfolio images or videos yet</p>
                </div>
              )}
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              {vendor.reviews?.length > 0 ? (
                <div className="space-y-6">
                  {vendor.reviews.map((review) => (
                    <div key={review.review_id} className="card-vendor" data-testid={`review-${review.review_id}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-[#2D2D52]/40 flex-shrink-0">
                          {review.consumer_picture ? (
                            <img src={review.consumer_picture} alt={review.consumer_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-600">
                              <Users className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-stone-200 font-outfit font-medium">{review.consumer_name}</span>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-4 h-4 ${i < review.rating ? 'text-[#FCD34D] fill-amber-500' : 'text-stone-700'}`} 
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-[#9CA3AF] font-outfit">{review.comment}</p>
                          <span className="text-[#64748B] text-sm mt-2 block">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card-vendor text-center py-12">
                  <Star className="w-12 h-12 text-stone-700 mx-auto mb-4" />
                  <p className="text-[#9CA3AF] font-outfit">No reviews yet</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
      <ReportIssueModal
        open={showReport}
        onClose={() => setShowReport(false)}
        vendorId={vendorId}
        vendorName={vendor?.business_name || vendor?.owner_name}
      />
      {showBookPicker && (
        <BookNowPicker vendor={vendor} onClose={() => setShowBookPicker(false)} />
      )}
      {zoomOpen && (vendor.profile_image || vendor.user_picture) && (
        <div
          className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setZoomOpen(false)}
          data-testid="profile-zoom-backdrop"
        >
          <img
            src={(() => { const src = vendor.profile_image || vendor.user_picture; return src.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src; })()}
            alt={vendor.business_name}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full rounded-2xl shadow-[0_20px_60px_-20px_rgba(168,85,247,0.6)]"
            data-testid="profile-zoom-image"
          />
          <button
            type="button"
            onClick={() => setZoomOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Close zoom"
            data-testid="profile-zoom-close"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
